// Ingredient catalogue — the shared nutrition vocabulary.
//
// Ingredients live in the SHARED partition under SK `ingredient#<slug>`, written
// by the scraper and (optionally) by imports here. They are the single source
// of nutrition truth referenced by per-user inventory, recipes and menus.
//
// Stored energy is per-100g in kJ. Display conversion to kcal is done here so
// the rest of the app can work in the user's preferred unit.

import { getItem, listItems, updateItem, deleteItem, SHARED_PARTITION_KEY } from './db.js';
import { slugifyName } from './slug.js';

/** SK prefix for ingredient (nutrition) records in the shared partition. */
export const INGREDIENT_PREFIX = 'ingredient#';

/** 1 kcal = 4.184 kJ (thermochemical). */
const KJ_PER_KCAL = 4.184;

export const ENERGY_UNITS = Object.freeze({ KCAL: 'cal', KJ: 'kj' });

/** Build the canonical ingredient itemId for a given food name. */
export function ingredientIdForName(name) {
  return `${INGREDIENT_PREFIX}${slugifyName(name)}`;
}

/** Convert kilojoules to kilocalories. */
export function kjToKcal(kj) {
  return kj === null || kj === undefined ? null : kj / KJ_PER_KCAL;
}

/** Convert kilocalories to kilojoules. */
export function kcalToKj(kcal) {
  return kcal === null || kcal === undefined ? null : kcal * KJ_PER_KCAL;
}

/**
 * Energy (in kJ) for a whole serving of an ingredient, derived from its
 * per-100g energy and serving weight. Returns null if either is missing.
 */
export function servingKj(ingredient) {
  const kj100 = ingredient?.kj100g;
  const weight = ingredient?.weightPerServe;
  if (kj100 === null || kj100 === undefined) return null;
  if (weight === null || weight === undefined) return null;
  return (Number(kj100) * Number(weight)) / 100;
}

/**
 * Grams of a macronutrient ("protein" | "fat" | "carbs") in a whole serving of
 * an ingredient, derived from its per-100g value and serving weight. Returns
 * null if either the macro or the weight is missing.
 */
export function servingMacro(ingredient, macro) {
  const per100 = ingredient?.[`${macro}100g`];
  const weight = ingredient?.weightPerServe;
  if (per100 === null || per100 === undefined) return null;
  if (weight === null || weight === undefined) return null;
  return (Number(per100) * Number(weight)) / 100;
}

/**
 * Format an energy value (stored in kJ) for display in the given unit.
 * Returns a rounded number; callers add the unit label.
 */
export function formatEnergy(kj, unit = ENERGY_UNITS.KCAL) {
  if (kj === null || kj === undefined) return null;
  const value = unit === ENERGY_UNITS.KJ ? Number(kj) : kjToKcal(Number(kj));
  return Math.round(value);
}

/** Fetch a single ingredient record by its itemId, or null. */
export function getIngredient(doc, itemId) {
  return getItem(doc, SHARED_PARTITION_KEY, itemId);
}

/** Fetch a single ingredient record by food name, or null. */
export function getIngredientByName(doc, name) {
  return getIngredient(doc, ingredientIdForName(name));
}

/** List all ingredient records in the shared catalogue. */
export function listIngredients(doc) {
  return listItems(doc, SHARED_PARTITION_KEY, INGREDIENT_PREFIX);
}

/**
 * Ensure a shared ingredient record exists for the given name, creating it
 * (upsert) with the supplied nutrition attributes if absent. Returns the
 * resolved itemId. Used by imports so a meal not yet in the catalogue still
 * becomes referenceable. Existing records are left untouched.
 */
export async function ensureIngredient(doc, name, attrs = {}) {
  const itemId = ingredientIdForName(name);
  const existing = await getIngredient(doc, itemId);
  if (existing) return itemId;
  await updateItem(doc, SHARED_PARTITION_KEY, itemId, {
    name,
    createdAt: new Date().toISOString(),
    ...attrs,
  });
  return itemId;
}

/**
 * Nutrition + descriptive fields persisted for an ingredient. Energy is stored
 * per-100g in kJ (`kj100g`); macros per-100g in grams. `null` is allowed for
 * any unknown value so partial records are representable.
 */
const INGREDIENT_FIELDS = [
  'name',
  'servingSuggestion',
  'weightPerServe',
  'kj100g',
  'protein100g',
  'fat100g',
  'carbs100g',
  'shelfLifeDays',
  // Buying / pantry:
  //  buyServings  — servings per purchasable pack; 0 = cannot be bought.
  //  buyUnit      — pack noun for the shopping list (e.g. "loaf"); defaults to
  //                 "serving" at display time when absent.
  //  pantryStaple — true => treated as infinite stock (never bought/decremented).
  'buyServings',
  'buyUnit',
  'pantryStaple',
  'tags',
  'image',
  // Free-text Markdown notes (null = none). The weekly LnE scrape carries
  // existing notes forward (scraper handler), so they survive its full-item Put.
  'notes',
];

/** Default servings-per-pack when an ingredient hasn't specified one. */
export const DEFAULT_BUY_SERVINGS = 1;
/** Default pack unit noun for the shopping list when none is set. */
export const DEFAULT_BUY_UNIT = 'serving';

/**
 * Servings contained in one purchasable pack of an ingredient. Defaults to 1.
 * A value of 0 means the ingredient cannot be bought.
 */
export function buyServingsOf(ingredient) {
  const v = ingredient?.buyServings;
  return v === null || v === undefined ? DEFAULT_BUY_SERVINGS : Number(v);
}

/** Whether an ingredient can be purchased (buyServings > 0 and always-available
 * ingredients — pantry staples / restaurant items — are never bought). */
export function isBuyable(ingredient) {
  if (isAlwaysAvailable(ingredient)) return false;
  return buyServingsOf(ingredient) > 0;
}

/** Whether an ingredient is an always-in-stock pantry staple. */
export function isPantryStaple(ingredient) {
  return ingredient?.pantryStaple === true;
}

/**
 * Whether an ingredient is a "restaurant" item — food procured directly from a
 * restaurant at the time it's eaten. Detected by a case-insensitive `restaurant`
 * tag. Like a pantry staple, it's assumed always available: never pooled, sourced,
 * bought, added to stock/the shopping list, or given back.
 */
export function isRestaurant(ingredient) {
  return (ingredient?.tags ?? []).some((t) => String(t).toLowerCase() === 'restaurant');
}

/**
 * Whether an ingredient should be treated as infinite / always on hand — the
 * union of pantry staples and restaurant items. Every sourcing, buying and
 * shopping-list path gates on this so both kinds are handled identically.
 */
export function isAlwaysAvailable(ingredient) {
  return isPantryStaple(ingredient) || isRestaurant(ingredient);
}

/**
 * Create or update a manually-managed shared ingredient.
 *
 * ID scheme: manually-created ingredients get a random UUID id
 * (`ingredient#<uuid>`) so renaming never breaks references from recipes or
 * inventory. (LnE ingredients keep their deterministic `ingredient#<slug>` id,
 * written by the scraper / matched on menu import — those are not created here.)
 *
 * Pass `existing` (the current record) to edit in place — its `itemId` is kept,
 * only the supplied fields are updated. Pass `null` to create. `fields` carries
 * the values from INGREDIENT_FIELDS; absent keys are left untouched on edit.
 * Returns the resolved itemId.
 */
export async function saveIngredient(doc, existing, fields) {
  const itemId = existing?.itemId ?? `${INGREDIENT_PREFIX}${crypto.randomUUID()}`;

  const attrs = {};
  for (const key of INGREDIENT_FIELDS) {
    if (key in fields) attrs[key] = fields[key];
  }
  if (!existing) attrs.createdAt = new Date().toISOString();
  attrs.updatedAt = new Date().toISOString();

  await updateItem(doc, SHARED_PARTITION_KEY, itemId, attrs);
  return itemId;
}

/** Delete a shared ingredient record by its itemId. */
export function deleteIngredient(doc, itemId) {
  return deleteItem(doc, SHARED_PARTITION_KEY, itemId);
}

/**
 * Collect the distinct tags used across a set of ingredients, sorted
 * case-insensitively. Handy for tag autocomplete suggestions.
 */
export function collectIngredientTags(ingredients) {
  const seen = new Map(); // lowercased -> original casing (first seen)
  for (const ing of ingredients ?? []) {
    for (const t of ing.tags ?? []) {
      const key = t.toLowerCase();
      if (!seen.has(key)) seen.set(key, t);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
